/**
 * ResiliationDialog — Workflow complet en 3 étapes :
 *   1. Upload  : glisser les pièces de résiliation (LRAR, AR, enveloppe)
 *   2. Preview : compléter le formulaire + aperçu HTML de la lettre d'acceptation
 *   3. Confirm : valider → génère lettre + stocke docs + met contrat en en_cours
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Upload, FileText, Loader2, ArrowLeft, ArrowRight, X, Eye, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

export default function ResiliationDialog({ open, onOpenChange, contract, onDone }) {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    date_demande_resiliation: "",
    reference_ar: "",
    signataire_nom: "",
    signataire_fonction: "",
    duree_preavis_mois: 0,
    corps_lettre: "",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setFiles([]); setUploaded([]);
    setPreview(null); setBusy(false);
    setForm({
      date_demande_resiliation: "", reference_ar: "",
      signataire_nom: "", signataire_fonction: "",
      duree_preavis_mois: 0, corps_lettre: "",
    });
  };
  const closeAll = () => { reset(); onOpenChange(false); };

  const onPickFiles = (list) => {
    const arr = Array.from(list || []).filter(
      (f) => (f.type === "application/pdf" || f.type.startsWith("image/"))
              && f.size <= 10 * 1024 * 1024);
    setFiles(arr);
  };

  const uploadFiles = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post(
        `/contracts/${contract.id}/demande-resiliation/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(r.data.uploaded || []);
      toast.success(`${r.data.count} pièce(s) uploadée(s)`);
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const loadPreview = async () => {
    if (!form.date_demande_resiliation) {
      toast.error("Date de la LRAR requise"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/contracts/${contract.id}/demande-resiliation/preview-lettre`, form);
      setPreview(r.data);
      setStep(3);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const ids = uploaded.map((u) => u.file_asset_id);
      const qs = ids.map((i) => `file_asset_ids=${encodeURIComponent(i)}`).join("&");
      const r = await api.post(
        `/contracts/${contract.id}/demande-resiliation/confirm?${qs}`, form);
      toast.success(`Résiliation validée — effet au ${r.data.date_effet}`);
      closeAll();
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); else onOpenChange(true); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
                       data-testid="resiliation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Demande de résiliation — {contract?.title}
            <Badge variant="outline" className="ml-2 text-[10px]">Étape {step}/3</Badge>
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          {[
            { n: 1, label: "Upload pièces" },
            { n: 2, label: "Formulaire" },
            { n: 3, label: "Aperçu & valider" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step > s.n ? "bg-emerald-100 text-emerald-700"
                : step === s.n ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-400"}`}>
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={step >= s.n ? "text-slate-900 font-medium" : "text-slate-400"}>{s.label}</span>
              {i < 2 && <span className="text-slate-300 mx-1">→</span>}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Uploadez la <b>lettre recommandée avec accusé de réception</b> et
              toute pièce complémentaire (AR postal, enveloppe). PDF, PNG, JPG — 10 Mo max.
            </p>
            <label className="block cursor-pointer p-8 text-center border-2 border-dashed rounded-xl
                                border-slate-200 hover:border-blue-400 hover:bg-blue-50/30"
                      data-testid="resiliation-upload-zone">
              <input id="resiliation-file-input" name="resiliation-file-input" type="file" accept="application/pdf,image/*" multiple className="hidden"
                        onChange={(e) => onPickFiles(e.target.files)}
                        data-testid="resiliation-file-input" />
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm font-medium text-slate-700">
                {files.length ? `${files.length} fichier(s) prêt(s)` : "Glisser les pièces ici ou cliquer"}
              </p>
            </label>
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600 p-2 bg-slate-50 rounded">
                    <FileText className="h-3 w-3 text-slate-400" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-slate-400">{Math.round(f.size / 1024)} KB</span>
                    <button onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))}
                            className="text-red-500 hover:underline">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
              ✓ {uploaded.length} pièce(s) uploadée(s)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date de la LRAR *</Label>
                <div className="mt-1">
                  <DatePickerFR value={form.date_demande_resiliation}
                                  onChange={(v) => setForm({ ...form, date_demande_resiliation: v })}
                                  data-testid="resiliation-date-lrar" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Référence AR (n° recommandé)</Label>
                <Input value={form.reference_ar}
                        onChange={(e) => setForm({ ...form, reference_ar: e.target.value })}
                        placeholder="Ex : 1A 234 567 890 1"
                        className="mt-1"
                        data-testid="resiliation-ref-ar" />
              </div>
              <div>
                <Label className="text-xs">Nom du signataire</Label>
                <Input value={form.signataire_nom}
                        onChange={(e) => setForm({ ...form, signataire_nom: e.target.value })}
                        placeholder="Ex : Jean Dupont"
                        className="mt-1"
                        data-testid="resiliation-signataire-nom" />
              </div>
              <div>
                <Label className="text-xs">Fonction du signataire</Label>
                <Input value={form.signataire_fonction}
                        onChange={(e) => setForm({ ...form, signataire_fonction: e.target.value })}
                        placeholder="Ex : Directeur Général"
                        className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Durée du préavis (mois)</Label>
                <Input type="number" min={0} max={24}
                        value={form.duree_preavis_mois}
                        onChange={(e) => setForm({ ...form, duree_preavis_mois: parseInt(e.target.value, 10) || 0 })}
                        className="mt-1 font-mono"
                        data-testid="resiliation-preavis" />
                <p className="text-[10px] text-slate-400 mt-1">
                  0 = effet immédiat. La date d'effet sera max(date_demande + préavis, date_fin_contrat).
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Corps de la lettre d'acceptation (optionnel — laissez vide pour utiliser le modèle)</Label>
              <Textarea rows={4} value={form.corps_lettre}
                          onChange={(e) => setForm({ ...form, corps_lettre: e.target.value })}
                          placeholder="Laissez vide pour utiliser le modèle standard"
                          className="mt-1" />
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && preview && (
          <div className="space-y-4">
            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
              <b>Date d'effet calculée :</b> {preview.date_effet_fr} (max entre date_demande + préavis et date_fin_contrat)
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-medium border-b flex items-center gap-2">
                <Eye className="h-3 w-3" /> Aperçu de la lettre d'acceptation
              </div>
              <iframe
                srcDoc={preview.html}
                sandbox="allow-same-origin"
                className="w-full h-[500px] bg-white"
                title="Aperçu lettre"
                data-testid="resiliation-preview-iframe" />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-row justify-between items-center gap-2">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)}
                       data-testid="resiliation-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={closeAll}>Annuler</Button>
            {step === 1 && (
              <Button onClick={uploadFiles} disabled={!files.length || uploading}
                       className="bg-blue-600 hover:bg-blue-700 text-white"
                       data-testid="resiliation-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Uploader et continuer <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={loadPreview} disabled={busy || !form.date_demande_resiliation}
                       className="bg-blue-600 hover:bg-blue-700 text-white"
                       data-testid="resiliation-preview-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                Générer l'aperçu <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={confirm} disabled={busy}
                       className="bg-red-600 hover:bg-red-700 text-white"
                       data-testid="resiliation-confirm-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Valider la résiliation
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
